import React, { useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { useDropzone } from "react-dropzone";
import styled from "styled-components";

const FileUploader = forwardRef(
    (
        {
            acceptedFileTypes,
            onFileUpload,
            maxFileSize,
            existingFileUrl,
            existingFileName,
            existingFileType,
            onRemoveExisting,
        },
        ref,
    ) => {
        const [file, setFile] = useState(null);
        const [error, setError] = useState("");
        const [previewUrl, setPreviewUrl] = useState(null);

        const formatFileSize = (bytes) => {
            if (bytes < 1024) return bytes + " bytes";
            else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
            else return (bytes / 1048576).toFixed(1) + " MB";
        };

        const onDrop = useCallback(
            (acceptedFiles, rejectedFiles) => {
                if (rejectedFiles.length > 0) {
                    if (
                        rejectedFiles[0].errors[0]?.code === "file-too-large" &&
                        maxFileSize
                    ) {
                        setError(
                            `File is too large. Max size is ${formatFileSize(maxFileSize)}.`
                        );
                    } else {
                        const allowedExtensions = Object.values(acceptedFileTypes).flat();
                        setError(
                            `Invalid file type. Please upload ${allowedExtensions.join(
                                ", "
                            )} files only.`
                        );
                    }
                } else if (acceptedFiles.length > 0) {
                    const selectedFile = acceptedFiles[0];
                    setFile(selectedFile);
                    setError("");

                    // Create preview URL for images
                    if (selectedFile.type.startsWith("image/")) {
                        const url = URL.createObjectURL(selectedFile);
                        setPreviewUrl(url);
                    } else {
                        setPreviewUrl(null);
                    }

                    onFileUpload(selectedFile);
                }
            },
            [acceptedFileTypes, onFileUpload, maxFileSize]
        );

        const { getRootProps, getInputProps, open } = useDropzone({
            onDrop,
            accept: acceptedFileTypes,
            multiple: false,
            maxSize: maxFileSize,
        });

        useImperativeHandle(ref, () => ({
            reset: () => {
                setFile(null);
                setError("");
                if (previewUrl) {
                    URL.revokeObjectURL(previewUrl);
                    setPreviewUrl(null);
                }
            },
        }));

        const getFileIcon = () => {
            if (!file) return "📄";
            const fileExtension = file.name.split(".").pop()?.toLowerCase() || "";
            switch (fileExtension) {
                case "xls":
                case "xlsx":
                case "csv":
                    return "📊";
                case "pdf":
                    return "📕";
                case "jpg":
                case "jpeg":
                case "png":
                    return "🖼️";
                default:
                    return "📄";
            }
        };

        const handleRemoveFile = (e) => {
            e.stopPropagation();
            setFile(null);
            setError("");
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
            }
            if (onFileUpload) {
                onFileUpload(null);
            }
        };

        const handleRemoveExisting = (e) => {
            e.stopPropagation();
            if (onRemoveExisting) {
                onRemoveExisting();
            }
        };

        // Determine what to display
        const hasExistingFile = Boolean(existingFileUrl);
        const hasFile = file || hasExistingFile;
        const isExistingImage = existingFileType?.startsWith("image/");
        const displayImage = previewUrl || (isExistingImage ? existingFileUrl : null);
        const isImage = file?.type?.startsWith("image/") || isExistingImage;

        return (
            <UploaderContainer {...getRootProps()}>
                <input {...getInputProps()} />

                {hasFile ? (
                    <>
                        {isImage && displayImage ? (
                            <ImagePreview>
                                <img src={displayImage} alt="Preview" />
                                <ImageOverlay>
                                    <Button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            open();
                                        }}
                                    >
                                        Change Image
                                    </Button>
                                    <Button
                                        onClick={file ? handleRemoveFile : handleRemoveExisting}
                                    >
                                        Remove Image
                                    </Button>
                                </ImageOverlay>
                            </ImagePreview>
                        ) : (
                            <>
                                <FileIcon>{getFileIcon()}</FileIcon>
                                <FileName>
                                    {file?.name || existingFileName || "Existing payment form"}{" "}
                                    {file ? `(${formatFileSize(file.size)})` : ""}
                                </FileName>
                                <ButtonContainer>
                                    <Button onClick={open}>Change File</Button>
                                    <Button
                                        onClick={file ? handleRemoveFile : handleRemoveExisting}
                                    >
                                        Remove File
                                    </Button>
                                </ButtonContainer>
                            </>
                        )}
                        <DropText>Or drop a new file to replace</DropText>
                    </>
                ) : (
                    <>
                        <FileIcon>{getFileIcon()}</FileIcon>
                        <DropText>Drop your Files Here</DropText>
                        <BrowseText>
                            <span> Browse Files</span> from your Computer
                        </BrowseText>
                        {maxFileSize && (
                            <InfoText>Max file size: {formatFileSize(maxFileSize)}</InfoText>
                        )}
                    </>
                )}
                {error && <ErrorText>{error}</ErrorText>}
            </UploaderContainer>
        );
    }
);

FileUploader.displayName = "FileUploader";

const UploaderContainer = styled.div`
    border: 1px dashed #ccc;
    border-radius: 10px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: border 0.3s ease;

    &:hover {
        border-color: #888;
    }
`;

const FileIcon = styled.div`
    font-size: 48px;
    margin-bottom: 10px;
`;

const DropText = styled.p`
    font-size: 18px;
    margin-bottom: 10px;
`;

const BrowseText = styled.p`
    & span {
        color: #007bff;
    }
`;

const FileName = styled.p`
    font-size: 16px;
    margin-top: 10px;
`;

const ErrorText = styled.p`
    color: red;
    margin-top: 10px;
`;

const InfoText = styled.p`
    color: #666;
    font-size: 14px;
    margin-top: 10px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 10px;
    margin-top: 10px;
`;

const Button = styled.button`
    background-color: #007bff;
    color: white;
    border: none;
    padding: 5px 10px;
    border-radius: 5px;
    cursor: pointer;
    &:hover {
        background-color: #0056b3;
    }
`;

const ImagePreview = styled.div`
    position: relative;
    width: 100%;
    max-width: 300px;
    height: 200px;
    margin: 0 auto;
    border-radius: 8px;
    overflow: hidden;
    border: 2px solid #e5e7eb;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const ImageOverlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.3s ease;

    ${ImagePreview}:hover & {
        opacity: 1;
    }

    button {
        background-color: rgba(255, 255, 255, 0.9);
        color: #333;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;

        &:hover {
            background-color: white;
        }

        &:last-child {
            background-color: rgba(239, 68, 68, 0.9);
            color: white;

            &:hover {
                background-color: #dc2626;
            }
        }
    }
`;

export default FileUploader;
